import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { activateLogin, getLoginActivation, resendLoginActivation, type LoginActivationInfo } from '@/api/activation';
import { AuthApiError, clearTemporaryPasswordToken, getMockTemporaryPassword, getTemporaryPasswordToken, setFirstLoginPassword, setLoginNotice } from '@/api/auth';
import { ApiError } from '@/api/client';
import './onboarding.css';

const Activate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: activationToken } = useParams<{ token?: string }>();
  const setupToken = (location.state as { setupToken?: string } | null)?.setupToken || getTemporaryPasswordToken();
  const [activation, setActivation] = useState<LoginActivationInfo | null>(null);
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? sessionStorage.getItem('nova_login_prefill_email') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [activationLoading, setActivationLoading] = useState(Boolean(activationToken));
  const [resendLoading, setResendLoading] = useState(false);

  const activationValid = activation?.status === 'VALID'
    && (!activation.expiresAt || new Date(activation.expiresAt).getTime() > Date.now());

  useEffect(() => {
    if (!activationToken) return;
    void getLoginActivation(activationToken)
      .then(setActivation)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'This activation link is invalid or expired.'))
      .finally(() => setActivationLoading(false));
  }, [activationToken]);

  const checks = [
    { label: '8 to 20 characters', ok: password.length >= 8 && password.length <= 20 },
    { label: 'Upper and lower case letters', ok: /[a-z]/.test(password) && /[A-Z]/.test(password) },
    { label: 'At least one number', ok: /\d/.test(password) },
    { label: 'At least one special character', ok: /[^A-Za-z0-9]/.test(password) },
  ];

  const handleResend = async () => {
    if (!activation?.customerId) return;
    setResendLoading(true);
    setError('');
    try {
      const fresh = await resendLoginActivation(activation.customerId);
      navigate(`/login-activation/${encodeURIComponent(fresh.token)}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a new activation link.');
    } finally {
      setResendLoading(false);
    }
  };

  const passwordProblem = (): string => {
    if (password.length < 8 || password.length > 20) return 'Password must be 8 to 20 characters.';
    if (!checks.slice(1).every((check) => check.ok)) return 'Use upper and lower case, a number and a special character.';
    return '';
  };

  const handleSubmit = async () => {
    setError('');
    if (activationToken) {
      if (!activation || !activationValid) return setError('This activation link is invalid or expired.');
      if (!email.trim() || !email.includes('@')) return setError('Enter the email address registered with your account.');
    } else if (!setupToken) {
      navigate('/login', { replace: true });
      return;
    } else if (!email.trim() || !email.includes('@')) return setError('Enter the email address registered with your account.');
    const problem = passwordProblem();
    if (problem) return setError(problem);
    if (password !== confirm) return setError('Passwords do not match.');
    setLoading(true);
    try {
      if (activationToken) {
        await activateLogin(activationToken, { email: email.trim(), password });
      } else {
        await setFirstLoginPassword({ temporaryPassword: getMockTemporaryPassword(), newPassword: password, confirmPassword: confirm });
        clearTemporaryPasswordToken();
      }
      setLoginNotice(email.trim(), 'Your password is set. Log in with your new password.');
      setSuccess(true);
    } catch (err) {
      setError(err instanceof AuthApiError ? err.message : 'Could not set your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!activationToken && !setupToken) {
    return <Navigate to="/login" replace />;
  }

  if (activationToken && activationLoading) {
    return <div className="nova-ob-dark"><div className="nova-ob-card-dark"><div className="spinner-border" role="status" aria-label="Loading activation link" /></div></div>;
  }

  if (activationToken && (!activation || !activationValid)) {
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark">
          <h1 className="nova-ob-brand" style={{ fontSize: '1.4rem' }}>Activation link unavailable</h1>
          <p className="nova-ob-tagline">This activation link is invalid or expired. Request a fresh link to create your login.</p>
          {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
          {activation?.customerId ? (
            <button type="button" className="btn btn-light btn-lg w-100 fw-semibold" disabled={resendLoading} onClick={() => void handleResend()}>
              {resendLoading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
              Send a fresh link
            </button>
          ) : (
            <button type="button" className="btn btn-light btn-lg w-100 fw-semibold" onClick={() => navigate('/login', { replace: true })}>
              Return to login
            </button>
          )}
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="nova-ob-success-screen">
        <div className="nova-ob-tick" aria-hidden="true">✓</div>
        <h1 className="nova-ob-heading">Your password is ready</h1>
        <p>Log in with your email, Customer ID or mobile number and your new password.</p>
        <div className="nova-ob-footer">
          <div className="nova-ob-footer-inner">
            <button type="button" className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => navigate('/login', { replace: true, state: { email, notice: 'Your password is set. Log in with your new password.' } })}>
              Log in now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nova-ob-dark">
      <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left', position: 'relative' }}>
        <button type="button" className="btn-close position-absolute top-0 end-0 m-3 btn-close-white" aria-label="Close" onClick={() => {
          if (window.confirm('Leave without a new password?\n\nYou need your own password to use NovaBank. You can log in with the temporary password again while it is valid.')) {
            clearTemporaryPasswordToken();
            navigate('/welcome', { replace: true });
          }
        }} />
        <div className="text-center mb-4">
          <h1 className="nova-ob-brand" style={{ fontSize: '1.4rem' }}>{activationToken ? 'Create your login' : 'Set your new password'}</h1>
          <p className="nova-ob-tagline mb-0">{activationToken ? `Welcome, ${activation?.fullName ?? 'customer'}. Create your sign-in credentials to get started.` : 'You signed in with a temporary password. Choose your own to continue. After this you log in with your email and this password, then an OTP to your registered mobile.'}</p>
        </div>

        <div className="form-floating mb-3 text-start">
          <input id="login-email" type="email" className="form-control" placeholder="Email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} />
          <label htmlFor="login-email">Email</label>
        </div>

        <div className="form-floating mb-2 text-start position-relative">
          <input id="new-password" type={showPassword ? 'text' : 'password'} className="form-control" placeholder="New password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} />
          <label htmlFor="new-password">New password</label>
          <button type="button" className="btn btn-link position-absolute end-0 top-50 translate-middle-y" style={{ color: 'var(--nova-blue)' }} aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)}>
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>

        <ul className="list-unstyled mb-3" style={{ fontSize: '0.85rem' }}>
          {checks.map((check) => <li key={check.label} className={check.ok ? 'text-success' : 'text-white-50'}>{check.ok ? '✓' : '○'} {check.label}</li>)}
        </ul>

        <div className="form-floating mb-3 text-start">
          <input id="confirm-password" type="password" className="form-control" placeholder="Confirm password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(''); }} />
          <label htmlFor="confirm-password">Confirm password</label>
        </div>

        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
        <div className="alert alert-info py-2">{activationToken ? 'Use the email address registered with your account. NovaBank will never ask for your password on a call.' : 'It must be different from the temporary password and must not contain your email name. NovaBank will never ask for your password on a call.'}</div>
        <button type="button" className="btn btn-light btn-lg w-100 fw-semibold" disabled={loading || !password || !confirm} onClick={() => void handleSubmit()}>
          {loading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
          {activationToken ? 'Create login' : 'Set password'}
        </button>
      </div>
    </div>
  );
};

export default Activate;
